import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Download, TrendingUp, PlayCircle, FileStack, ListChecks, AlertTriangle } from 'lucide-react';
import { useOffline } from '../../contexts/OfflineContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Kpi = ({ label, value }) => (
  <div className="bg-white rounded-lg border p-3" data-testid={`mgmt-kpi-${label.replace(/\s+/g, '-').toLowerCase()}`}>
    <p className="text-xs text-slate-500">{label}</p><p className="text-xl font-bold text-slate-800">{value ?? '—'}</p>
  </div>
);

export default function QSManagement({ projectId, domain, accent }) {
  const isQ = domain === 'QUALITY';
  const { qsConflicts, clearQsConflicts } = useOffline();
  const [dash, setDash] = useState(null);
  const [ai, setAi] = useState(null);
  const [starters, setStarters] = useState([]);
  const [myday, setMyday] = useState(null);
  const durl = isQ ? 'quality/management/dashboard' : 'safety/management/dashboard';
  const load = () => {
    axios.get(`${API}/projects/${projectId}/${durl}`).then((r) => setDash(r.data)).catch(() => {});
    axios.get(`${API}/projects/${projectId}/qs/ai/stats`, { params: { domain } }).then((r) => setAi(r.data)).catch(() => {});
    axios.get(`${API}/projects/${projectId}/qs/starter-templates`, { params: { domain } }).then((r) => setStarters(r.data)).catch(() => {});
    axios.get(`${API}/projects/${projectId}/qs/my-day`).then((r) => setMyday(r.data)).catch(() => {});
  };
  useEffect(load, [projectId, domain]);
  const dl = async (type, fmt = 'csv') => {
    try {
      const r = await axios.get(`${API}/projects/${projectId}/qs/reports/${type}.${fmt}`, { responseType: 'blob' });
      const u = URL.createObjectURL(r.data); const a = document.createElement('a'); a.href = u; a.download = `${type}-register.${fmt}`; a.click(); URL.revokeObjectURL(u);
    } catch (e) { toast.error('Export not permitted'); }
  };
  const clone = async (code) => { try { const r = await axios.post(`${API}/projects/${projectId}/qs/starter-templates/${code}/clone`); toast.success(`Cloned ${r.data.name} v${r.data.version} — edit & version it`); } catch (e) { toast.error('Not permitted'); } };
  const escalate = async () => { try { const r = await axios.post(`${API}/projects/${projectId}/qs/escalation/run`); toast.success(`Escalation: ${r.data.ncrs} NCRs, ${r.data.correctiveActions} actions flagged`); } catch (e) { toast.error('Not permitted'); } };

  const qReports = [['ncr', 'NCR Register'], ['quality-observations', 'Quality Observations'], ['inspections', 'Inspection Register'], ['corrective-actions', 'Corrective Actions']];
  const sReports = [['safety-observations', 'Safety Observations'], ['corrective-actions', 'Corrective Actions']];
  const reports = isQ ? qReports : sReports;

  return (
    <div className="space-y-6" data-testid="qs-management">
      <div>
        <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2 mb-3"><TrendingUp className="w-5 h-5" /> Management Intelligence <span className="text-xs text-slate-400 font-normal">(factual · no composite scores)</span></h2>
        {dash && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {isQ ? (
              <>
                <Kpi label="Open NCRs" value={dash.openNCRs} /><Kpi label="Overdue NCRs" value={dash.overdueNCRs} />
                <Kpi label="Awaiting Response" value={dash.ncrsAwaitingResponse} /><Kpi label="Awaiting Verify" value={dash.ncrsAwaitingVerification} />
                <Kpi label="Reinspection Pending" value={dash.reinspectionPending} /><Kpi label="Open CAPAs" value={dash.openCorrectiveActions} />
                <Kpi label="Overdue CAPAs" value={dash.overdueCorrectiveActions} /><Kpi label="Avg Closure Days" value={dash.avgClosureDurationDays} />
              </>
            ) : (
              <>
                <Kpi label="Open Observations" value={dash.openObservations} /><Kpi label="Near Misses" value={dash.nearMisses} />
                <Kpi label="High Risk" value={dash.highRisk} /><Kpi label="Critical Risk" value={dash.criticalRisk} />
                <Kpi label="Awaiting Verify" value={dash.awaitingVerification} /><Kpi label="Residual High/Crit" value={dash.residualHighCritical} />
                <Kpi label="Open CAPAs" value={dash.openCorrectiveActions} /><Kpi label="Overdue Actions" value={dash.overdueSafetyActions} />
              </>
            )}
          </div>
        )}
        {dash?.ncrAgeing && <p className="text-xs text-slate-500 mt-2">NCR ageing (days): {Object.entries(dash.ncrAgeing).map(([k, v]) => `${k}:${v}`).join('  ·  ')}</p>}
        {dash?.repeatFindings?.length > 0 && <p className="text-xs text-amber-700 mt-1">Repeat findings (confirmed records): {dash.repeatFindings.map((r) => `${r.category}×${r.count}`).join(', ')}</p>}
      </div>

      <div>
        <h3 className="text-sm font-semibold text-slate-700 mb-2">Reports & Exports</h3>
        <div className="space-y-2">
          {reports.map(([t, label]) => (
            <div key={t} className="flex items-center gap-2 flex-wrap" data-testid={`mgmt-export-row-${t}`}>
              <span className="text-xs text-slate-500 w-40">{label}</span>
              <button onClick={() => dl(t, 'csv')} data-testid={`mgmt-export-${t}`} className="flex items-center gap-1 text-xs px-3 py-1.5 border rounded-lg"><Download className="w-3.5 h-3.5" /> CSV</button>
              <button onClick={() => dl(t, 'xlsx')} data-testid={`mgmt-export-xlsx-${t}`} className="flex items-center gap-1 text-xs px-3 py-1.5 border rounded-lg"><Download className="w-3.5 h-3.5" /> XLSX</button>
            </div>
          ))}
        </div>
      </div>

      {qsConflicts && qsConflicts.length > 0 && (
        <div className="border border-amber-300 bg-amber-50 rounded-lg p-3" data-testid="mgmt-offline-conflicts">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-amber-800 flex items-center gap-1"><AlertTriangle className="w-4 h-4" /> Offline Sync Conflicts ({qsConflicts.length})</h3>
            <button onClick={clearQsConflicts} data-testid="mgmt-clear-conflicts" className="text-xs text-amber-700 underline">Dismiss</button>
          </div>
          <p className="text-[11px] text-amber-700 mb-2">These fields changed on the server while you were offline. Non-conflicting fields were auto-merged; review the values below.</p>
          <ul className="space-y-1">
            {qsConflicts.map((c, i) => (
              <li key={i} className="text-xs text-amber-800" data-testid={`mgmt-conflict-${i}`}>
                • {c.text || c.field || c.itemId} — server: <b>{String(c.serverValue)}</b> · yours: <b>{String(c.clientValue ?? c.expected)}</b>
              </li>
            ))}
          </ul>
        </div>
      )}

      {ai && (
        <div>
          <h3 className="text-sm font-semibold text-slate-700 mb-2">AI Review Statistics</h3>
          <p className="text-xs text-slate-600" data-testid="mgmt-ai-stats">Analyses: {ai.analysesPerformed} · Pending: {ai.pendingReview} · Accepted: {ai.accepted} · Corrected: {ai.corrected} · Rejected: {ai.rejected} · Insufficient: {ai.insufficientEvidence} · Unavailable: {ai.aiUnavailable}{ai.acceptanceRate != null ? ` · Acceptance rate: ${(ai.acceptanceRate * 100).toFixed(0)}%` : ''}</p>
          <p className="text-[10px] text-slate-400">{ai.acceptanceRateDefinition}</p>
        </div>
      )}

      {myday && (
        <div>
          <h3 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-1"><ListChecks className="w-4 h-4" /> My Day</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="mgmt-myday">
            <Kpi label="Inspection Requests" value={(myday.inspectionRequests || []).length} />
            <Kpi label="My Inspections" value={(myday.assignedInspections || []).length} />
            <Kpi label="NCR Actions" value={(myday.ncrActions || []).length} />
            <Kpi label="Overdue" value={myday.overdue} />
          </div>
        </div>
      )}

      <div>
        <h3 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-1"><FileStack className="w-4 h-4" /> Starter Template Library <span className="text-[10px] text-slate-400 font-normal">— clone & configure for your project; not a statutory standard</span></h3>
        <div className="flex flex-wrap gap-2">
          {starters.map((s) => (
            <button key={s.code} onClick={() => clone(s.code)} data-testid={`mgmt-clone-${s.code}`} className={`text-xs px-3 py-2 border rounded-lg bg-${accent}-50`}>{s.name} · Clone</button>
          ))}
        </div>
      </div>

      <button onClick={escalate} data-testid="mgmt-run-escalation" className="flex items-center gap-1 text-xs px-3 py-2 border border-amber-300 text-amber-700 rounded-lg"><PlayCircle className="w-4 h-4" /> Run Overdue Escalation (idempotent)</button>
    </div>
  );
}
